import React, { useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type ProgramStatusSeriesPoint = {
  date: string; // YYYY-MM-DD
  upcoming: number;
  ongoing: number;
  completed: number;
};

export default function ProgramStatusOverviewChart({
  series,
  totals,
  series7d,
  totals7d
}: {
  series: ProgramStatusSeriesPoint[] | undefined;
  totals?: { upcoming: number; ongoing: number; completed: number };
  series7d?: ProgramStatusSeriesPoint[] | undefined;
  totals7d?: { upcoming: number; ongoing: number; completed: number };
}) {
  const canvasRef = useRef<any>(null);
  const [range, setRange] = React.useState<"30d" | "7d">("30d");

  // Compute totals if not provided
  const activeSeries = range === "30d" ? series : series7d;
  const activeTotalsProp = range === "30d" ? totals : totals7d;

  const computedTotals = useMemo(() => {
    if (activeTotalsProp) {
      return activeTotalsProp as { upcoming: number; ongoing: number; completed: number };
    }
    const base = { upcoming: 0, ongoing: 0, completed: 0 };
    (activeSeries ?? []).forEach(p => {
      base.upcoming += p.upcoming;
      base.ongoing += p.ongoing;
      base.completed += p.completed;
    });
    return base;
  }, [activeSeries, activeTotalsProp]);

  const labels = ["Upcoming", "Ongoing", "Completed"];
  const values = [
    computedTotals.upcoming,
    computedTotals.ongoing,
    computedTotals.completed
  ];
  const colors = ["#F59E0B", "#22C55E", "#3B82F6"];

  const data = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          label: "Number of Programs",
          data: values,
          backgroundColor: colors,
          borderRadius: 8,
          borderWidth: 0,
          barThickness: 56,
          maxBarThickness: 72,
          categoryPercentage: 0.5,
          barPercentage: 0.9
        }
      ]
    } as const;
  }, [values]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: "#E5E7EB", drawBorder: false },
        ticks: { stepSize: 5, font: { size: 12 } },
        title: { display: true, text: "Number of Programs", font: { size: 12 } }
      }
    }
  };

  const totalText = computedTotals
    ? `U: ${computedTotals.upcoming}  |  O: ${computedTotals.ongoing}  |  C: ${computedTotals.completed}`
    : undefined;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 h-[480px] flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-slate-900">Program Status Overview</h3>
        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={e => setRange(e.target.value as any)}
            className="rounded-md px-4 py-2.5 text-sm appearance-none bg-white pr-8 focus:outline-none"
            style={{
              borderColor: "#D1D5DB",
              color: "#6B7280",
              borderWidth: "1px",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.5rem center",
              backgroundSize: "12px 12px"
            }}
          >
            <option value="30d">Last 30 Days</option>
            <option value="7d">Last 7 Days</option>
          </select>
        </div>
      </div>
      <div className="h-[380px]">
        {/* @ts-ignore */}
        <Bar ref={canvasRef as any} data={data as any} options={options as any} />
      </div>
      {totalText && (
        <div className="mt-2 text-xs text-gray-500 self-end">{totalText}</div>
      )}
    </div>
  );
}


